import { useMemo } from 'react';
import { type RpcResult } from '@chainflip/rpc/common';
import { type ChainflipAsset, getInternalAsset } from '@chainflip/utils/chainflip';
import {
  type StrategyType,
  type StrategyInfoFragment,
} from '@/shared/graphql/generated/reporting/graphql';
import { getActiveStrategies } from '@/shared/graphql/reporting/strategies';
import useChainflipAssetPrices from '@/shared/hooks/useChainflipAssetPrices';
import { useGqlQuery } from '@/shared/hooks/useGqlQuery';
import useRpcRequest from '@/shared/hooks/useRpcRequest';
import {
  getStrategyFromReportingFragment,
  getStrategyTemplateKey,
  type TradingStrategy,
} from '@/shared/utils/strategies';
import TokenAmount from '@/shared/utils/TokenAmount';

const getTradingStrategyKey = (
  strategy:
    | { TickZeroCentered: { spread_tick: number } }
    | { SimpleBuySell: { buy_tick: number; sell_tick: number } }
    | {
        InventoryBased: {
          min_buy_tick: number;
          max_buy_tick: number;
          min_sell_tick: number;
          max_sell_tick: number;
        };
      }
    | {
        OracleTracking: {
          min_buy_offset_tick: number;
          max_buy_offset_tick: number;
          min_sell_offset_tick: number;
          max_sell_offset_tick: number;
        };
      },
) => {
  if ('TickZeroCentered' in strategy) {
    return `${-strategy.TickZeroCentered.spread_tick}_${strategy.TickZeroCentered.spread_tick}`;
  }
  if ('SimpleBuySell' in strategy) {
    return `${strategy.SimpleBuySell.buy_tick}_${strategy.SimpleBuySell.sell_tick}`;
  }
  if ('InventoryBased' in strategy) {
    return `${strategy.InventoryBased.min_buy_tick}_${strategy.InventoryBased.max_buy_tick}_${strategy.InventoryBased.min_sell_tick}_${strategy.InventoryBased.max_sell_tick}`;
  }
  if ('OracleTracking' in strategy) {
    return `${strategy.OracleTracking.min_buy_offset_tick}_${strategy.OracleTracking.max_buy_offset_tick}_${strategy.OracleTracking.min_sell_offset_tick}_${strategy.OracleTracking.max_sell_offset_tick}`;
  }
  throw new Error(`Unknown trading strategy type: ${Object.keys(strategy)[0]}`);
};

const getTradingStrategyDetails = (
  strategy: RpcResult<'cf_get_trading_strategies'>[0]['strategy'],
): { baseAsset: ChainflipAsset; key: string; type: StrategyType } => {
  if ('TickZeroCentered' in strategy) {
    return {
      baseAsset: getInternalAsset(strategy.TickZeroCentered.base_asset),
      key: getTradingStrategyKey(strategy),
      type: 'TICK_ZERO_CENTERED',
    };
  }
  if ('SimpleBuySell' in strategy) {
    return {
      baseAsset: getInternalAsset(strategy.SimpleBuySell.base_asset),
      key: getTradingStrategyKey(strategy),
      type: 'SIMPLE_BUY_SELL',
    };
  }
  if ('InventoryBased' in strategy) {
    return {
      baseAsset: getInternalAsset(strategy.InventoryBased.base_asset),
      key: getTradingStrategyKey(strategy),
      type: 'INVENTORY_BASED',
    };
  }
  if ('OracleTracking' in strategy) {
    return {
      baseAsset: getInternalAsset(strategy.OracleTracking.base_asset),
      key: getTradingStrategyKey(strategy),
      type: 'ORACLE_TRACKING',
    };
  }
  throw new Error(`Unknown trading strategy type: ${Object.keys(strategy)[0]}`);
};

export const useActiveStrategies = () => {
  const rpcResult = useRpcRequest({
    method: 'cf_get_trading_strategies',
    args: [],
    refetchInterval: 60_000,
  });

  const reportingResult = useGqlQuery(getActiveStrategies, {
    context: { clientName: 'reportingService' },
    select: (data) =>
      data.strategies?.nodes.reduce(
        (acc, cur) => ({ ...acc, [cur.strategyAccountSs58Id]: cur }),
        {} as Record<string, StrategyInfoFragment | undefined>,
      ),
    refetchInterval: 60_000,
  });

  const { prices: assetPrices } = useChainflipAssetPrices();

  const data = useMemo(
    () =>
      rpcResult.data?.map((strategy): TradingStrategy => {
        const reportingStrategy = reportingResult.data?.[strategy.strategy_id];
        const quoteAsset = 'Usdc';
        const { baseAsset, key, type } = getTradingStrategyDetails(strategy.strategy);

        const balances = strategy.balance.reduce(
          (acc, [rpcAsset, rpcAmount]) => {
            const asset = getInternalAsset(rpcAsset);
            return { ...acc, [asset]: TokenAmount.fromAsset(rpcAmount, asset) };
          },
          {} as Record<ChainflipAsset, TokenAmount | undefined>,
        );
        const baseLiquidity = balances[baseAsset] ?? TokenAmount.fromAsset(0, baseAsset);
        const quoteLiquidity = balances[quoteAsset] ?? TokenAmount.fromAsset(0, quoteAsset);

        const baseLiquidityUsd = assetPrices[baseAsset]
          ? baseLiquidity.mul(assetPrices[baseAsset]).toNumber()
          : Number(reportingStrategy?.baseLiquidityValueUsd ?? 0);
        const quoteLiquidityUsd = assetPrices[quoteAsset]
          ? quoteLiquidity.mul(assetPrices[quoteAsset]).toNumber()
          : Number(reportingStrategy?.quoteLiquidityValueUsd ?? 0);

        // if the reporting service does not know the strategy, we assume that the strategy was just created
        const reportingData = reportingResult.data
          ? (getStrategyFromReportingFragment(reportingStrategy)?.reportingData ?? {
              filledVolumeUsd: 0,
              pnlUsd: 0,
              pnl24HourUsd: 0,
              apyPercent: 0,
              projectedReturnUsd: 0,
              createdAtBlockTimestamp: new Date().toISOString(),
              closedAtBlockTimestamp: undefined,
              closedAtBlockId: undefined,
              closedAtEventIndex: undefined,
            })
          : undefined;

        const templateKey = getStrategyTemplateKey({
          baseAsset,
          type,
          key,
        });

        return {
          templateKey,
          baseAsset,
          quoteAsset,
          type,
          baseLiquidity,
          quoteLiquidity,
          baseLiquidityUsd,
          quoteLiquidityUsd,
          totalLiquidityUsd: baseLiquidityUsd + quoteLiquidityUsd,
          strategyAccountSs58Id: strategy.strategy_id,
          ownerAccountSs58Id: strategy.lp_id,
          reportingData,
          status: reportingStrategy?.closedAtBlockId ? 'CLOSED' : 'ACTIVE',
        };
      }),
    [rpcResult.data, reportingResult.data, assetPrices],
  );

  return { data, isLoading: rpcResult.isLoading, isReportingLoading: reportingResult.isLoading };
};
